<template>
  <div class="app-page apply-page">
    <div class="apply-header">
      <router-link to="/friend" class="back-btn">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
      </router-link>
      <h2 class="page-title">好友申请</h2>
    </div>

    <el-tabs v-model="activeTab" class="apply-tabs">
      <el-tab-pane label="收到的申请" name="received">
        <div v-if="receivedApplies.length > 0" class="apply-list">
          <div v-for="apply in receivedApplies" :key="apply.id" class="apply-item">
            <img :src="apply.fromAvatar || defaultAvatar" class="apply-avatar" alt="">
            <div class="apply-info">
              <span class="apply-name">{{ apply.fromNickname }}</span>
              <span class="apply-reason" v-if="apply.applyReason">{{ apply.applyReason }}</span>
              <span class="apply-time">{{ formatTime(apply.createdTime) }}</span>
            </div>
            <div class="apply-actions" v-if="apply.status === 0">
              <button class="accept-btn" @click="handleApply(apply, true)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                同意
              </button>
              <button class="reject-btn" @click="handleApply(apply, false)">拒绝</button>
            </div>
            <span v-else class="apply-status" :class="{ accepted: apply.status === 1, rejected: apply.status === 2 }">
              {{ apply.status === 1 ? '已同意' : '已拒绝' }}
            </span>
          </div>
        </div>
        <div v-else class="empty-state">
          <p>暂无收到的好友申请</p>
        </div>
      </el-tab-pane>
      <el-tab-pane label="发出的申请" name="sent">
        <div v-if="sentApplies.length > 0" class="apply-list">
          <div v-for="apply in sentApplies" :key="apply.id" class="apply-item">
            <img :src="apply.fromAvatar || defaultAvatar" class="apply-avatar" alt="">
            <div class="apply-info">
              <span class="apply-name">{{ apply.fromNickname }}</span>
              <span class="apply-reason" v-if="apply.applyReason">{{ apply.applyReason }}</span>
              <span class="apply-time">{{ formatTime(apply.createdTime) }}</span>
            </div>
            <span class="apply-status" :class="{ accepted: apply.status === 1, rejected: apply.status === 2 }">
              {{ apply.status === 0 ? '待处理' : apply.status === 1 ? '已同意' : '已拒绝' }}
            </span>
          </div>
        </div>
        <div v-else class="empty-state">
          <p>暂无发出的好友申请</p>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import friendApi from '@/api/friend'

const defaultAvatar = 'https://picsum.photos/id/1005/200'
const activeTab = ref('received')
const receivedApplies = ref([])
const sentApplies = ref([])
const userId = ref(null)

onMounted(() => {
  const stored = localStorage.getItem('userId')
  userId.value = stored ? Number(stored) : null
  if (userId.value) {
    fetchApplies()
  }
})

const fetchApplies = async () => {
  try {
    const [receivedRes, sentRes] = await Promise.all([
      friendApi.getReceivedApplies(userId.value),
      friendApi.getSentApplies(userId.value)
    ])
    if (receivedRes.data.success) {
      receivedApplies.value = receivedRes.data.data || []
    }
    if (sentRes.data.success) {
      sentApplies.value = sentRes.data.data || []
    }
  } catch (error) {
    console.error('获取申请列表失败:', error)
  }
}

const handleApply = async (apply, accept) => {
  try {
    const res = await friendApi.handleApply(apply.id, userId.value, accept)
    if (res.data.success) {
      ElMessage.success(accept ? '已同意好友申请' : '已拒绝好友申请')
      fetchApplies()
    } else {
      ElMessage.warning(res.data.msg || '操作失败')
    }
  } catch (error) {
    ElMessage.error('操作失败')
  }
}

const formatTime = (time) => {
  if (!time) return ''
  const d = new Date(time)
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
</script>

<style scoped>
.apply-page {
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 16px;
}
.apply-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.back-btn {
  display: flex;
  padding: 4px;
  color: var(--app-text-primary);
  text-decoration: none;
}
.page-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: var(--app-text-primary);
}
.apply-tabs {
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-lg);
  border: 1px solid var(--app-border-subtle);
  padding: 16px;
}
.apply-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.apply-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 12px;
  border-radius: var(--app-radius-md);
  transition: background 0.2s;
}
.apply-item:hover {
  background: var(--app-bg-soft);
}
.apply-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
}
.apply-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.apply-name {
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--app-text-primary);
}
.apply-reason {
  font-size: 0.8125rem;
  color: var(--app-text-muted);
}
.apply-time {
  font-size: 0.75rem;
  color: var(--app-text-muted);
  opacity: 0.7;
}
.apply-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.accept-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: none;
  border-radius: var(--app-radius-full);
  background: #2ecc71;
  color: white;
  cursor: pointer;
  font-size: 0.8125rem;
  transition: all 0.2s;
}
.accept-btn:hover {
  opacity: 0.85;
}
.reject-btn {
  padding: 6px 14px;
  border: 1px solid var(--app-border-light);
  border-radius: var(--app-radius-full);
  background: transparent;
  color: var(--app-text-muted);
  cursor: pointer;
  font-size: 0.8125rem;
  transition: all 0.2s;
}
.reject-btn:hover {
  border-color: #e74c3c;
  color: #e74c3c;
}
.apply-status {
  font-size: 0.8125rem;
  color: var(--app-text-muted);
  padding: 4px 10px;
  border-radius: var(--app-radius-full);
  background: var(--app-bg-soft);
}
.apply-status.accepted {
  color: #2ecc71;
  background: rgba(46, 204, 113, 0.1);
}
.apply-status.rejected {
  color: #e74c3c;
  background: rgba(231, 76, 60, 0.1);
}
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--app-text-muted);
}
</style>
